import LegalLayout, { LegalHero, LegalSection, LegalList } from "@/components/LegalLayout";

export default function PrivacyPage() {
  return (
    <>
      <LegalHero
        eyebrow="LEGAL · PRIVACY POLICY"
        title="Privacy Policy"
        subtitle="Plain-English version of what we collect, why, who touches it, and the rights you have over your data."
        updated="2026"
      />
      <LegalLayout>
        <LegalSection id="collected" title="1. Data We Collect">
          <p>We collect the minimum information needed to run RADAR for you:</p>
          <LegalList items={[
            "Login information — name, email address, and Google profile picture, received via Google OAuth.",
            "Account research inputs — company names, websites, geography, industry, contacts, pain points, and past interactions you enter into the report form.",
            "User-added notes — primary-data inputs you provide during the 'Enhance' workflow (past projects, internal strengths, insider knowledge).",
            "Usage history — which reports you generated, when, which sections you exported, and pipeline activities you logged.",
            "Subscription &amp; billing data — current plan, billing cycle, renewal date, and (in Phase 2) payment confirmations from Razorpay / Stripe.",
            "Technical telemetry — IP address, browser user-agent, request timestamps — used solely for security monitoring and abuse prevention.",
          ]} />
        </LegalSection>

        <LegalSection id="purpose" title="2. Purpose of Collection">
          <p>We use the data above to:</p>
          <LegalList items={[
            "Generate AI-powered intelligence reports tailored to your inputs.",
            "Produce proposition drafts, CXO outreach copy, and PPT storylines.",
            "Track quota usage and enforce plan limits.",
            "Provide CRM pipeline analytics + team productivity rollups for managers.",
            "Process subscription payments and renewals via our PCI-compliant payment partners.",
            "Improve RADAR's prompts, AI orchestration, and product UX (using aggregated, de-identified usage patterns).",
            "Detect abuse, fraud, and unauthorised access.",
          ]} />
        </LegalSection>

        <LegalSection id="third-party" title="3. Third-Party Processing">
          <p>
            RADAR is built on a curated set of vetted partners. We share only the minimum data
            necessary with each to deliver the service:
          </p>
          <LegalList items={[
            "Google OAuth — authenticates you via your Google account. We receive your email, name, and profile picture only.",
            "Anthropic (Claude Haiku 4.5) — the LLM that synthesises your intelligence reports. We send the report inputs + public-web context; Anthropic does not train on your data per their enterprise terms.",
            "Tavily — real-time web search. We send the queries (anonymous; no user identity). Used purely for citation grounding.",
            "Razorpay (Phase 2) — Indian payment processor. We share order details; never your card.",
            "Stripe (Phase 2, international) — payment processor for non-India billing. PCI-compliant tokenisation; no card data stored on our servers.",
            "Resend — transactional email delivery (weekly digest, account notifications).",
            "MongoDB Atlas — managed database hosting; encrypted at rest, encrypted in transit.",
          ]} />
          <p>
            We never sell your data to third parties. We never use your account research inputs or
            primary-data notes for advertising or marketing personalisation.
          </p>
        </LegalSection>

        <LegalSection id="retention" title="4. Data Retention">
          <p>
            While your subscription is active, we retain all of your account data, reports, and CRM
            history. When a subscription expires, your account enters <strong className="text-white">read-only mode for 1 year</strong> —
            you can sign in and view historical reports, but cannot generate new ones.
          </p>
          <p>
            After the 1-year retention window, accounts are archived: personal identifiers are removed
            from associated reports / activities, and minimal billing metadata is retained for legal
            and tax compliance. You may request immediate deletion at any time (see Section 6).
          </p>
        </LegalSection>

        <LegalSection id="security" title="5. Security">
          <LegalList items={[
            "All traffic is served over HTTPS / TLS 1.3.",
            "Session tokens are httpOnly, Secure, SameSite-strict cookies — never readable by JavaScript.",
            "MongoDB clusters are encrypted at rest with regional isolation.",
            "Role-based server-side access — BDRs cannot see other users' pipeline; managers see consolidated team views.",
            "Quota enforcement is server-side and race-safe (atomic Mongo updates with unique indexes).",
            "Secrets and API keys live in environment variables, never in source code or client bundles.",
          ]} />
        </LegalSection>

        <LegalSection id="rights" title="6. Your Rights">
          <p>Wherever you are, you can:</p>
          <LegalList items={[
            "Request a copy of all data we hold about you (data export).",
            "Request permanent deletion of your account and associated data.",
            "Cancel your subscription at any time without giving a reason.",
            "Ask us to correct inaccurate information.",
            "Withdraw consent for non-essential processing.",
          ]} />
          <p>
            EU/UK users: you additionally have the rights granted under GDPR (Article 15–22) — access,
            rectification, erasure, restriction, portability, and objection. India users: the rights
            described in the Digital Personal Data Protection Act, 2023, apply by reference.
          </p>
          <p>
            To exercise any of these rights, email <a className="text-[#0A84FF] hover:underline" href="mailto:support@radaracctintel.com">support@radaracctintel.com</a> from
            the address registered with your account. We aim to respond within 5 business days.
          </p>
        </LegalSection>

        <LegalSection id="cookies" title="7. Cookies">
          <p>
            We use exactly one functional cookie — <code className="font-mono-data text-[#0A84FF]">session_token</code> —
            to keep you signed in. We do not run advertising trackers or third-party cookies. PostHog
            product analytics may be used in aggregate, identified-only mode, to improve UX.
          </p>
        </LegalSection>

        <LegalSection id="changes" title="8. Changes to This Policy">
          <p>
            Material changes will be announced via email and posted with an updated "Last updated"
            date on this page.
          </p>
        </LegalSection>

        <LegalSection id="contact" title="9. Contact">
          <p>
            Privacy questions, data export requests, or deletion requests:
            <a className="text-[#0A84FF] hover:underline ml-1" href="mailto:support@radaracctintel.com">support@radaracctintel.com</a>.
          </p>
        </LegalSection>
      </LegalLayout>
    </>
  );
}
